import { Injectable } from '@angular/core';
import {
  DataQueryRule,
  FactDefinitionTypes,
  FieldTypes,
  FilterExpressions,
  getOperator,
  getSymbol,
  GroupBys,
  IFactAndValue,
  IFieldMeta,
  LogicalOperators,
  MathService,
  AdvancedMathService,
  MoreSection,
  NodeKind,
  Operators,
  OrderByDirections,
  QueryAggregations,
  QueryDataTypes,
  Selection,
  Selections,
  ValueKinds
} from '@optima-stride-ai/core';
import { faker } from '@faker-js/faker';

@Injectable({
  providedIn: 'root'
})
export class DqrTransformationMethodsService {
  // Static map of aggregation display values to enum values - handles enum changes
  private static readonly AGGREGATION_VALUE_MAP: Record<string, QueryAggregations> = {
    'Average': QueryAggregations.AVERAGE,
    'Median': QueryAggregations.MEDIAN,
    'Count': QueryAggregations.COUNT,
    'Sum': QueryAggregations.SUM,
    'Max': QueryAggregations.MAX,
    'Min': QueryAggregations.MIN,
    'Avg': QueryAggregations.AVERAGE  // Handle alternative value
  };


  transformLogFieldToDQRFactandValues(logFieldsMeta: IFieldMeta[]): IFactAndValue[] {
    const factAndValues: IFactAndValue[] = logFieldsMeta.map((fieldMeta: IFieldMeta) => {
      return {
        fact: {
          name: fieldMeta.name,
          columnAlias: fieldMeta.name
        },
        dataType: this.mapFieldTypeToQueryDataType(fieldMeta.type),
        valueKind: ValueKinds.NONE,
        aggregationFunction: undefined,
        isReference: false
      };
    });

    factAndValues.push({
      fact: {
        name: 'datetime',
        columnAlias: 'date'
      },
      dataType: QueryDataTypes.DATE,
      valueKind: ValueKinds.NONE,
      aggregationFunction: undefined,
      isReference:false
    });
    return factAndValues;
  }

  transformOrderByToFormat(selections: Selections): any[] {
    const orderBy = selections.selects
      .filter((selection: Selection) => selection.orderBy !== 'NONE')
      .map((selection: Selection) => {
        const fact = selection.factAndValue.fact;
        return {
          columnAlias: fact.columnAlias || fact.name,
          orderType: selection.orderBy
        };
      });

    const nonDatetimeOrderBy = orderBy.filter((item: any) =>
      (item?.columnAlias || '').toString().toLowerCase() !== 'datetime' && (item?.columnAlias || '').toString().toLowerCase() !== 'date'
    );

    return [
      ...nonDatetimeOrderBy,
      {
        columnAlias: 'date',
        orderType: OrderByDirections.DESC
      }
    ];
  }

  transformSelectionsToFormat(
    selections: Selections,
    preDefinedReferences: IFactAndValue[] = [],
    selectedStatusFieldAlias?: string | null,
    isProgressKpi: boolean = false
  ): any[] {
    const emptyValueDynamicReferences = selections.dynamicReferences.filter((reference: IFactAndValue) => {
      const value = reference?.value;
      return value === undefined || value === null || value.toString().trim() === '';
    });
    const combinedReferences = [
      ...preDefinedReferences,
      ...emptyValueDynamicReferences
    ];
    const referenceMap = this.buildPreDefinedReferenceMap(combinedReferences);

    return selections.selects.map((selection: Selection) => {
      const fact = selection.factAndValue.fact;
      const dataType = selection.factAndValue.dataType;
      const isAggregation = selection.factDefinitionType === FactDefinitionTypes.Aggregation;
      const isFormula = selection.factDefinitionType === FactDefinitionTypes.Formula;
      const rawFormulaValue = (selection.factAndValue.value || '').toString();
      const factName = (selection.factAndValue.fact.name || '').toLowerCase();
      const factAlias = (selection.factAndValue.fact.columnAlias || '').toLowerCase();
      const isDateFact = factName === 'datetime' || factAlias === 'datetime' || factAlias === 'date';
      const isStatusFact = !!selectedStatusFieldAlias && (factAlias === selectedStatusFieldAlias.toLowerCase());
     // console.log("Is this a date fact",isDateFact)
      const isReference = isProgressKpi
        ? !(isDateFact || isStatusFact)
        : selection.factAndValue.isReference;
       // console.log("Is this a reference",isReference)
       // console.log("Is this a progress KPI",isProgressKpi)
      return {
        fact: {
          columnName: fact.name,
          columnAlias: fact.columnAlias || fact.name,
          isTableColumn: fact.name === 'datetime'
        },
        columnType: this.mapQueryDataTypeToString(dataType),
        //TEMP FIX : AVG(Reverted) and checkForAggregationInFormula
         ...(isAggregation ? { aggregationType: selection.factAndValue.aggregationFunction } : {}),
        ...(isFormula ? { value: this.replaceFormulaWordsWithFactAndValue(rawFormulaValue, referenceMap) } : {}),
        isAggregatedFormula: isAggregation || this.checkForAggregationInFormula(this.replaceFormulaWordsWithFactAndValue(rawFormulaValue, referenceMap)),
        isAggregationDistinct: isAggregation ? selection.isAggregationDistinct : false,
        isReference
      };
    });
  }

 private buildPreDefinedReferenceMap(preDefinedReferences: IFactAndValue[]): Map<string, IFactAndValue> {
  const map = new Map<string, IFactAndValue>();

  preDefinedReferences.forEach((reference: IFactAndValue) => {
    // Key by fact.name
    const name = reference?.fact?.name;
    if (name) {
      map.set(name, reference);
    }
    
    // ALSO key by columnAlias (for formulas referencing aliases like "lines_tested")
    const alias = reference?.fact?.columnAlias;
    if (alias && alias !== name) {
      map.set(alias, reference);
    }
  });
  return map;
}

  private getAggregationFunctionByValue(aggregationValue: string): QueryAggregations | undefined {
    return DqrTransformationMethodsService.AGGREGATION_VALUE_MAP[(aggregationValue || '')];
  }

  private replaceFormulaWordsWithFactAndValue(formula: string, referenceMap: Map<string, IFactAndValue>): string {
    if (!formula) {
      return '';
    }

    // Extract words from formula and replace only exact identifier tokens.
    return formula.replace(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g, (word: string) => {
      const reference = referenceMap.get(word);
      if (!reference) {
        return word;
      }
      const factJson : any = 
        {fact: {
          columnName: reference.fact?.name || '',
          columnAlias: reference.fact?.columnAlias || reference.fact?.name || '',
          isTableColumn: reference.fact?.name === 'datetime'
        },
        columnType: this.mapQueryDataTypeToString(reference.dataType),
        valueKind: reference.valueKind || '',
        aggregationFunction: reference.aggregationFunction || '',
        //isAggregatedFormula : this.checkForAggregationInFormula(reference.aggregationFunction || '')
      };
      // Create plain template string without JSON.stringify to avoid double escaping
      const serialized = `{'fact':{'columnName':'${factJson.fact.columnName}','columnAlias':'${factJson.fact.columnAlias}','isTableColumn':${factJson.fact.isTableColumn}},'columnType':'${factJson.columnType}','valueKind':'${factJson.valueKind}','aggregationType':'${factJson.aggregationFunction}'}`;
      
      // Wrap with unique delimiter for identification
      return `~{: ${serialized}   :}~`;
    });
  }

  private deserializeFormulaValue(serializedFormula: string): string {
    if (!serializedFormula) {
      return '';
    }

    // Match delimited template objects and extract columnName, columnAlias, and aggregationType
    // Pattern: ~{: {'fact':{'columnName':'...','columnAlias':'...',...},...,'aggregationType':'...'} :}~
    // If aggregationType has value, return columnAlias (user-defined alias), otherwise return columnName
    return serializedFormula.replace(
      /~\{:\s*\{'fact':\{'columnName':'([^']+)','columnAlias':'([^']+)'[^}]*\}[^}]*'aggregationType':'([^']*)'\}\s*:}~\s*/g,
      (match: string, columnName: string, columnAlias: string, aggregationType: string) => {
        // If has aggregation type, return the alias (what user typed)
        return aggregationType ? columnAlias : columnName;
      }
    );
  }

  private checkForAggregationInFormula(formula: string): boolean {
    if (!formula) {
      return false;
    }
    // Check if any fact in the formula has a non-empty aggregationType
    return /'aggregationType':'[^']+'/g.test(formula);
  }

  transformFilterExpressionsToFormat(filterExpressions: FilterExpressions, preDefinedReferences: IFactAndValue[] = []): any {
   // console.log("Filter expressions to transform - ", filterExpressions);
    const referenceMap = this.buildPreDefinedReferenceMap(preDefinedReferences);
    
    const mapFact = (fact: any) => ({
      columnName: fact?.name ?? null,
      columnAlias: fact?.columnAlias ?? null,
     tableAlias: "ML"
    });

    const mapColumnType = (value: any) => {
      return value ? this.mapQueryDataTypeToString(value) : null;
    };

    const mapExpression = (expression: any) => {
      const rawValue = expression.rightFact?.value ?? null;
      const isFormula = expression.rightFact?.valueKind === ValueKinds.FORMULA;
      const transformedValue = isFormula ? this.replaceFormulaWordsWithFactAndValue(rawValue, referenceMap) : rawValue;
      
      return {
        leftOperand: {
          fact: mapFact(expression.leftFact?.fact),
          columnType: mapColumnType(expression.leftFact?.dataType),
          isValueStatic: expression.leftFact?.valueKind === ValueKinds.STATIC,
          valueKind: expression.leftFact?.valueKind,
        },
        operator: getSymbol(expression.operator as Operators),
        rightOperand: {
          value: transformedValue,
          fact: expression.rightFact?.valueKind === ValueKinds.STATIC? {columnName:  null,
        tableAlias:  null} : mapFact(expression.rightFact?.fact),
          valueKind: expression.rightFact?.valueKind,
         // columnType: mapColumnType(expression.rightFact?.dataType),
          isValueStatic: expression.rightFact?.valueKind === ValueKinds.STATIC
        }
      };
    };

    const mapCondition = (conditionNode: any): any => ({
      condition: (conditionNode?.condition || []).map((item: any) => {
        return item?.kind === NodeKind.expression ? mapExpression(item) : mapCondition(item);
      }),
      logicalOperator: conditionNode?.logicalOperator || LogicalOperators.And
    });

    if (!filterExpressions?.condition?.condition?.length) {
      return null;
    }

    return mapCondition(filterExpressions.condition);
  }

  prepareDqrParameters(
    selections: Selections,
    filterExpressions: FilterExpressions,
    groupBys: GroupBys,
    more: MoreSection,
    preDefinedReferences: IFactAndValue[] = [],
    selectedStatusFieldAlias: string | null = null,
    isProgressKpi: boolean = false
  ): any {
    const selectionsFormatted = this.transformSelectionsToFormat(
      selections,
      preDefinedReferences,
      selectedStatusFieldAlias,
      isProgressKpi
    );
    const orderByFormatted = this.transformOrderByToFormat(selections);
    const filterExpressionsFormatted = this.transformFilterExpressionsToFormat(filterExpressions, preDefinedReferences);
    // console.log('Prepared DQR Parameters:', {
    //   selections: selectionsFormatted,
    //   orderBy: orderByFormatted,
    //   filter: filterExpressionsFormatted,
    //   limit: more.limit > 0 ? more.limit : null,
    //   statusFieldName: selectedStatusFieldAlias,
    //   isProgressKpi
    // });
    return {
      selection: selectionsFormatted.length ? selectionsFormatted : null,
      orderBy: orderByFormatted.length ? orderByFormatted : null,
      filter: filterExpressionsFormatted,
      limit: more.limit > 0 ? more.limit : null,
      statusFieldName : selectedStatusFieldAlias
    };
  }

  populateDQRFromAPIFormat(bizRulesData: any): DataQueryRule {
  //  console.log('Populating DQR from API format:', bizRulesData);
    const selections = this.convertAPISelectionsToClass(bizRulesData.selection, bizRulesData.orderBy);
    const filterExpressions = this.convertAPIFilterToClass(bizRulesData.filter);
   // console.log('converted filter expressions:', filterExpressions);
    const groupBys = new GroupBys([]);
    const more = new MoreSection(bizRulesData.limit || 0);
   // console.log('Populated DQR Selections:', selections);
    const rule =  new DataQueryRule(
      selections,
      filterExpressions,
      groupBys,
      more,
      new AdvancedMathService()
    );
    return rule;
  }

  getStatusFieldAliasFromAPIFormat(bizRulesData: any): string | null {
    return bizRulesData?.statusFieldName || null;
  }

  private convertAPISelectionsToClass(apiSelection: any[], apiOrderBy: any[]): Selections {
    if (!apiSelection || apiSelection.length === 0) {
      return new Selections([], [], new AdvancedMathService(), false, '');
    }

    const orderByMap = new Map<string, string>();
    if (apiOrderBy) {
      apiOrderBy.forEach((ob: any) => {
        orderByMap.set(ob.columnAlias, ob.orderType);
      });
    }

    const selections = apiSelection.map((sel: any) => {
      const columnType = sel.columnType;
      const columnAlias = sel.fact.columnAlias;
      const orderType = (orderByMap.get(columnAlias) as OrderByDirections) || OrderByDirections.NONE;
     // console.log("Aggregation Type ---",sel.aggregationType)
      const aggregationFunction = this.getAggregationFunctionByValue(sel.aggregationType);
     // console.log("Mapped Aggregation Function ---", aggregationFunction)
      const isFormula = sel.value;
      const formulaValue = isFormula ? this.deserializeFormulaValue(sel.value || '') : (sel.value || '');
      const isAggregationDistinct = sel.isAggregationDistinct || false;
      const isReference = sel.isReference;
    //  console.log("Converting API selection to class - ", sel);
      // console.log("Selection to convert into selections--",new Selection(
      //   {
      //     fact: {
      //       name: sel.fact.columnName,
      //       columnAlias: sel.fact.columnAlias || sel.fact.columnName
      //     },
      //     dataType: this.getDataType(columnType),
      //     valueKind: ValueKinds.NONE,
      //     value: formulaValue,
      //     aggregationFunction
      //   },
      //   faker.string.uuid(),
      //   sel.value ? FactDefinitionTypes.Formula : (sel.aggregationType && sel.isAggregatedFormula ? FactDefinitionTypes.Aggregation : FactDefinitionTypes.Field),
      //   orderType,
      //   false,
      //   false,
      //   false,
      //   false
      // ))
      return new Selection(
        {
          fact: {
            name: sel.fact.columnName,
            columnAlias: sel.fact.columnAlias || sel.fact.columnName
          },
          dataType: this.getDataType(columnType),
          valueKind: ValueKinds.NONE,
          value: formulaValue,
          aggregationFunction,
          isReference
        },
        faker.string.uuid(),
        sel.value ? FactDefinitionTypes.Formula : (sel.aggregationType && sel.isAggregatedFormula ? FactDefinitionTypes.Aggregation : FactDefinitionTypes.Field),
        orderType,
        false,
        isAggregationDistinct,
        false,
        false
      );
    });

    return new Selections(selections, [], new AdvancedMathService(), false, '');
  }

  private convertAPIFilterToClass(apiFilter: any): FilterExpressions {
    if (!apiFilter || !apiFilter.condition || apiFilter.condition.length === 0) {
      return new FilterExpressions(undefined, new AdvancedMathService(), false, '');
    }

    const parseLogicalOperator = (value: any): LogicalOperators => {
      const normalized = (value || '').toString().trim().toLowerCase();
      return normalized === 'or' ? LogicalOperators.Or : LogicalOperators.And;
    };

    const toFactAndValue = (operand: any, isLeftOperand: boolean) => {
      const fact = operand?.fact || {};
      const dataType = this.getDataType(operand?.columnType);
      const isValueStatic = !!operand?.isValueStatic;
      const hasFactName = !!fact?.columnName;
      const rawValue = operand?.value?.toString?.() ?? '';
      const deserializedValue = this.deserializeFormulaValue(rawValue);
      const valueKind = operand?.valueKind

      return {
        fact: {
          name: fact?.columnName || '',
          columnAlias: fact?.columnAlias || fact?.columnName || '',
          tableAlias: fact?.tableAlias || '',
          isTablecolumn: !!fact?.isTableColumn
        },
        dataType,
        valueKind,
        value: deserializedValue
      };
    };

    const parseExpression = (node: any) => {
      return {
        kind: NodeKind.expression,
        leftFact: toFactAndValue(node?.leftOperand, true),
        operator: getOperator((node?.operator || '=').toString()),
        rightFact: toFactAndValue(node?.rightOperand, false)
      };
    };

    const parseCondition = (node: any): any => {
      const items = Array.isArray(node?.condition) ? node.condition : [];
      return {
        kind: NodeKind.condition,
        logicalOperator: parseLogicalOperator(node?.logicalOperator),
        condition: items.map((item: any) => {
          return Array.isArray(item?.condition) ? parseCondition(item) : parseExpression(item);
        })
      };
    };

    const rootCondition = parseCondition(apiFilter);
    return new FilterExpressions(rootCondition, new AdvancedMathService(), false, '');
  }

  private mapFieldTypeToQueryDataType(fieldType: FieldTypes | undefined): QueryDataTypes {
    switch (fieldType) {
      case FieldTypes.number:
        case FieldTypes.currency:
        return QueryDataTypes.NUMBER;
      case FieldTypes.date:
      case FieldTypes.rangeDate:
        return QueryDataTypes.DATE;
      case FieldTypes.boolean:
        return QueryDataTypes.BOOLEAN;
      case FieldTypes.text:
      case FieldTypes.bizEntity:
      default:
        return QueryDataTypes.STRING;
    }
  }

  private getDataType(type: string): QueryDataTypes {
    switch (type) {
      case 'number':
        return QueryDataTypes.NUMBER;
      case 'timestamp':
        return QueryDataTypes.DATE;
      case 'boolean':
        return QueryDataTypes.BOOLEAN;
      case 'json':
        return QueryDataTypes.JSON;
      default:
        return QueryDataTypes.STRING;
    }
  }

  private mapQueryDataTypeToString(dataType: QueryDataTypes): string {
    switch (dataType) {
      case QueryDataTypes.NUMBER:
        return 'number';
      case QueryDataTypes.DATE:
        return 'timestamp';
      case QueryDataTypes.BOOLEAN:
        return 'boolean';
      case QueryDataTypes.STRING:
      default:
        return 'string';
    }
  }
}
